import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Search, Plus, Edit, Trash2, Truck, Phone, Mail,
  MapPin, Loader2, AlertTriangle, Eye
} from 'lucide-react';
import { skeletonKeys } from '../lib/listKeys';

const emptySupplier = { name: '', contact_person: '', email: '', phone: '', address: '', notes: '', status: 'active', lead_time_days: '' };

export default function SuppliersPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [formData, setFormData] = useState({ ...emptySupplier });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/suppliers', { params: { search, status: statusFilter, limit: 100 } });
      setSuppliers(res.data.suppliers || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(fetchSuppliers, 300);
    return () => clearTimeout(t);
  }, [fetchSuppliers]);

  const openAdd = () => {
    setEditSupplier(null);
    setFormData({ ...emptySupplier });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditSupplier(s);
    setFormData({
      name: s.name || '', contact_person: s.contact_person || '', email: s.email || '',
      phone: s.phone || '', address: s.address || '', notes: s.notes || '', status: s.status || 'active',
      lead_time_days: s.lead_time_days ?? ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) { toast.error('Supplier name is required'); return; }
    setSaving(true);
    try {
      // Coerce lead_time_days: empty string → null so backend keeps the fallback default.
      const payload = { ...formData };
      payload.lead_time_days = formData.lead_time_days === '' || formData.lead_time_days == null
        ? null : Number(formData.lead_time_days);
      if (editSupplier) {
        await api.put(`/suppliers/${editSupplier.supplier_id}`, payload);
        toast.success('Supplier updated');
      } else {
        await api.post('/suppliers', payload);
        toast.success('Supplier added');
      }
      setDialogOpen(false);
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (supplierId) => {
    try {
      await api.delete(`/suppliers/${supplierId}`);
      toast.success('Supplier deleted');
      setDeleteConfirm(null);
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Suppliers
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{total} suppliers</p>
        </div>
        <Button onClick={openAdd} className="gap-1.5 h-10" data-testid="add-supplier-btn">
          <Plus className="h-4 w-4" /> Add Supplier
        </Button>
      </div>

      <Card>
        <CardContent className="p-3 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search suppliers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="suppliers-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-36" data-testid="supplier-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card data-testid="suppliers-table-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Supplier</TableHead>
              <TableHead className="hidden md:table-cell">Contact</TableHead>
              <TableHead className="hidden lg:table-cell">Email / Phone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              skeletonKeys(3, 'supplier-skeleton').map((key) => (
                <TableRow key={key}><TableCell colSpan={5}><div className="h-10 bg-muted rounded animate-pulse" /></TableCell></TableRow>
              ))
            ) : suppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                  <Truck className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p>No suppliers found</p>
                </TableCell>
              </TableRow>
            ) : suppliers.map(s => (
              <TableRow key={s.supplier_id} data-testid={`supplier-row-${s.supplier_id}`}>
                <TableCell>
                  <div>
                    <p className="font-medium text-sm">{s.name}</p>
                    {s.address && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {s.address}</p>}
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm">{s.contact_person || '-'}</TableCell>
                <TableCell className="hidden lg:table-cell">
                  <div className="space-y-0.5">
                    {s.email && <p className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> {s.email}</p>}
                    {s.phone && <p className="text-xs flex items-center gap-1"><Phone className="h-3 w-3" /> {s.phone}</p>}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={s.status === 'active' ? 'outline' : 'secondary'} className={`text-xs ${s.status === 'active' ? 'text-emerald-600 dark:text-emerald-400 border-emerald-500/30' : ''}`}>
                    {s.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/suppliers/${s.supplier_id}`)} data-testid={`view-supplier-${s.supplier_id}`}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.supplier_id}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteConfirm(s.supplier_id)} data-testid={`delete-supplier-${s.supplier_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle>{editSupplier ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Supplier Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="e.g. MedSupply Corp" data-testid="supplier-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <Input value={formData.contact_person} onChange={(e) => setFormData(p => ({ ...p, contact_person: e.target.value }))} placeholder="John Smith" data-testid="supplier-contact-input" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(p => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="supplier-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="supplier@email.com" data-testid="supplier-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="+1-555-0100" data-testid="supplier-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={formData.address} onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))} placeholder="Full address" data-testid="supplier-address-input" />
            </div>
            <div className="space-y-2">
              <Label>Lead time (days)</Label>
              <Input
                type="number"
                min={1}
                max={90}
                value={formData.lead_time_days}
                onChange={(e) => setFormData(p => ({ ...p, lead_time_days: e.target.value }))}
                placeholder="Days from order to delivery — leave blank to use the planner default"
                data-testid="supplier-lead-time-input"
              />
              <p className="text-[11px] text-muted-foreground">Used by Purchase Planner. Blank falls back to the global default in the planner settings.</p>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes..." rows={2} data-testid="supplier-notes-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-supplier-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editSupplier ? 'Update' : 'Add'} Supplier
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-destructive" /> Delete Supplier</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Are you sure? This action cannot be undone. Suppliers with active POs cannot be deleted.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-supplier">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
